import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { supabase, sessionInitialized } from '../lib/supabase';
import { Mutex } from 'async-mutex';

const authMutex = new Mutex();

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string, inviteCode: string) => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
  isAdmin: () => boolean;
  clearSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isClearingSession, setIsClearingSession] = useState(false);

  const clearSession = async () => {
    if (isClearingSession) return;
    try {
      setIsClearingSession(true);
      await supabase.auth.signOut();
      
      // Clear all storage
      localStorage.clear();
      sessionStorage.clear();
      
      // Clear cookies
      document.cookie.split(';').forEach(cookie => {
        document.cookie = cookie
          .replace(/^ +/, '')
          .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
      });
      
      setUser(null);
      setIsLoading(false);
      window.location.href = '/login';
    } catch (error) {
      console.error('Clear session error:', error);
    } finally {
      setIsClearingSession(false);
    }
  };

  const login = async (email: string, password: string) => {
    const release = await authMutex.acquire();
    try {
      setIsLoading(true);
      setUser(null);
      await sessionInitialized;

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'login',
          email: email.trim().toLowerCase(),
          password,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Login failed');
      }

      const result = await response.json();

      await supabase.auth.setSession({
        access_token: result.session.access_token,
        refresh_token: result.session.refresh_token,
      });

      setUser(result.user);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
      release();
    }
  };

  const register = async (username: string, email: string, password: string, inviteCode: string) => {
    const release = await authMutex.acquire();
    try {
      setIsLoading(true);
      setUser(null);
      setIsRegistering(true);
      await sessionInitialized;

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'register',
          username,
          email: email.trim().toLowerCase(),
          password,
          inviteCode,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setIsLoading(false);
      setIsRegistering(false);
      release();
    }
  };

  const logout = async () => {
    const release = await authMutex.acquire();
    try {
      setIsLoading(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      await supabase.auth.signOut();
      setUser(null);
      
      // Clear all storage and cookies
      localStorage.clear();
      sessionStorage.clear();
      document.cookie.split(';').forEach(cookie => {
        document.cookie = cookie
          .replace(/^ +/, '')
          .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
      });
      
      if (session?.access_token) {
        try {
          await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/auth`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              action: 'logout',
            }),
          });
        } catch (error) {
          // Ignore logout logging errors
        }
      }
      
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      await clearSession();
    } finally {
      setIsLoading(false);
      release();
    }
  };

  const fetchUserData = async (accessToken: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          action: 'getUser',
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        if (error.code === 'refresh_token_not_found' || 
            response.status === 400 || 
            response.status === 401 || 
            error.message?.includes('Invalid token') || 
            error.message?.includes('JWT expired')) {
          await clearSession();
          return null;
        }
        throw new Error(error.error || 'Failed to get user data');
      }

      const { user: userData } = await response.json();
      return userData;
    } catch (error) {
      if (error instanceof Error && 
          (error.message.includes('Invalid token') || 
           error.message.includes('JWT expired') ||
           error.message.includes('Invalid authentication') ||
           error.message.includes('refresh_token_not_found'))) {
        await clearSession();
        return null;
      }
      throw error;
    }
  };

  useEffect(() => {
    let mounted = true;
    const initAuth = async () => {
      try {
        await sessionInitialized;
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          if (sessionError.message.includes('Invalid token') || 
              sessionError.message.includes('JWT expired') ||
              sessionError.message.includes('refresh_token_not_found')) {
            if (mounted) await clearSession();
            return;
          }
          throw sessionError;
        }

        if (!session?.user || isRegistering) {
          if (mounted) setUser(null);
          return;
        }

        if (mounted && session?.access_token) {
          try {
            const userData = await fetchUserData(session.access_token);
            if (userData) {
              setUser(userData);
            } else {
              await clearSession();
            }
          } catch (error) {
            console.error('Failed to fetch user data:', error);
            await clearSession();
          }
        }
      } catch (error) {
        console.error('Auth init error:', error);
        if (mounted) {
          await clearSession();
        }
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    const authListener = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted || isRegistering) return;

      try {
        if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
          setUser(null);
        } else if (session?.user && session?.access_token) {
          try {
            const userData = await fetchUserData(session.access_token);
            if (userData && mounted) {
              setUser(userData);
            } else {
              if (mounted && !isClearingSession) await clearSession();
            }
          } catch (error) {
            console.error('Auth state change error:', error);
            if (mounted && !isClearingSession) {
              await clearSession();
            }
          }
        } else {
          if (mounted) setUser(null);
        }
      } catch (error) {
        console.error('Auth state change error:', error);
        if (mounted && !isClearingSession) {
          await clearSession();
        }
      }
    });

    initAuth();

    return () => {
      mounted = false;
      authListener.data.subscription.unsubscribe();
    };
  }, [isRegistering, isClearingSession]);

  return (
    <AuthContext.Provider value={{
      user,
      login,
      register,
      logout,
      isLoading,
      isAdmin: () => user?.role === 'admin' || user?.role === 'superadmin',
      clearSession,
    }}>
      {children}
    </AuthContext.Provider>
  );
}